import {
  StorageService,
  SessionService,
  HTTPVerbs,
  generateRandomHex,
  request,
  type RequestOptions,
} from '@nextgen-web-framework/core';
import { CheckoutProfileService } from '@commerce-checkout-webcomponents/sdk';
import {
  CartIdReqPayloadType,
  ChangeQtyCartType,
  AddToCartReqPayloadType,
  ProjectionReqPayloadType,
  CreateCartPayloadType,
  GetProfilePayloadType,
  CartReqOptions,
  CartReqOtherOptions,
  CartConstantsEnum,
  ApiProjections,
} from '../../types';
import {
  OrderType,
  type Cart,
  type CartEntry,
  type SubCarts,
  CartEntryDetails,
  ErrorResponse,
  AccountSearch,
} from '../models';
import {
  getCartReqObj,
  CartId,
  EntryId,
  getCartsReqObj,
  createCartReqObj,
  deleteCartReqObj,
  getMiniCartReqObj,
  updateCartEntriesReqObj,
  getProfilesReqObj,
  addToCartGCOObj,
  updateGCOFlagReqObj,
  createSubCartReqObj,
  clearSubCartEntriesReqObj,
  SubCartId,
  deleteSubCartReqObj,
  getReassignCartReqObj,
  changeSubCartReqObj,
  getSubCartReqObj,
  removeQtySubCartReqObj,
  addToSubCartReqObj,
  applyCouponReqObj,
  updateCartReqObj,
  API_Version_CART,
  redeemReqObj,
} from '../config';
import {
  ApplyCounponsPayload,
  DeleteSubCartType,
  ReassignCartPayloadType,
  SubCartIdReqPayloadType,
  UpdateCartEntriesPayloadType,
  UpdateGCOFlagPayloadType,
  CartsByUserIdPayloadType,
  CartAPIResponse,
  GetCartsResponse,
  CreateSubCartPayloadType,
  CartIdWithMultipleEntryIdType,
  CustomRequestOptions,
  SubCartIdsReqPayloadType,
} from '../../types/cartTypes';
import {
  filterPromotionItemFromEntries,
  filterOutRegistrationProducts,
  isProxyOrder,
  validateBundleInEntries,
  requestWithVersionFix,
  createEmptyCart,
} from '../../utils';
import { getOrderTypeConfig } from '../../transformers';
import { UpdateCartRequest } from '../models/UpdateCartRequest';
import { Channel } from '../models/Channel';
import { RedeemAmPointRequest } from '../models/RedeemAmPointRequest';
import { RedeemAmPointResponse } from '../models/RedeemAmPointResponse';

type BodyInitWH = BodyInit & {
  warehouseId: string;
  deliveryType: string;
};

type requestOptPayloadType = CartReqOptions & CartIdReqPayloadType;

export const cartApi = {
  getCart(
    { options = {}, cartId, projections = ApiProjections.DETAILED }: requestOptPayloadType & ProjectionReqPayloadType,
    customOptions?: CustomRequestOptions,
  ): Promise<CartAPIResponse> {
    if (!cartId) {
      return Promise.resolve({
        code: 'UI-001',
        message: 'Please check arguments',
      });
    }
    const req = { ...getCartReqObj };
    req.url = req.url.replace(CartId, cartId);

    req.params = {
      projections: projections,
    };

    return handleRequest(req, customOptions, options);
  },
  getMiniCart(
    { options = {}, cartId }: requestOptPayloadType,
    customOptions?: CustomRequestOptions,
  ): Promise<CartAPIResponse> {
    if (!cartId) {
      return Promise.resolve({
        code: 'UI-001',
        message: 'Please check arguments',
      });
    }
    const req = { ...getMiniCartReqObj };

    req.url = req.url.replace(CartId, cartId);

    req.params = {
      projections: ApiProjections.DETAILED,
    };

    return handleRequest(req, customOptions, options);
  },
  changeQtyForEntryFromCart(
    { cartId, entryId, subCartId, payload, options = {} }: CartReqOptions & ChangeQtyCartType,
    customOptions?: CustomRequestOptions,
  ): Promise<CartAPIResponse> {
    if (!cartId || !entryId || !payload) {
      return Promise.resolve({
        code: 'UI-001',
        message: 'Please check arguments',
      });
    }

    const req = { ...changeSubCartReqObj };
    req.url = req.url.replace(CartId, cartId);
    req.url = req.url.replace(EntryId, String(entryId));
    let modifiedOptions = {};

    if (subCartId) {
      req.url = req.url.replace(SubCartId, subCartId);
      modifiedOptions = {
        ...options,
        skipCartIdStoreInLS: true,
      };
    }

    req.params = {
      projections: ApiProjections.DETAILED,
    };

    req.body = payload as unknown as BodyInit;

    return handleRequest(req, customOptions, modifiedOptions);
  },
  removeEntryFromCart(
    {
      cartId,
      subCartId,
      entryId,
      options = {},
    }: CartReqOptions & SubCartIdReqPayloadType & CartIdWithMultipleEntryIdType,
    customOptions?: CustomRequestOptions,
  ): Promise<CartAPIResponse> {
    if (!cartId || !entryId) {
      return Promise.resolve({
        code: 'UI-001',
        message: 'Please check arguments',
      });
    }
    const req = { ...removeQtySubCartReqObj };

    let modifiedOptions = {};
    if (subCartId) {
      req.url = req.url.replace(SubCartId, subCartId);
      modifiedOptions = {
        ...options,
        skipCartIdStoreInLS: true,
      };
    }
    req.url = req.url.replace(CartId, cartId);
    req.url = req.url.replace(EntryId, String(entryId));

    req.params = {
      projections: ApiProjections.DETAILED,
    };

    return handleRequest(req, customOptions, modifiedOptions);
  },
  getCarts(
    { options, projections = ApiProjections.DETAILED }: CartReqOptions & ProjectionReqPayloadType,
    customOptions?: CustomRequestOptions,
  ): Promise<GetCartsResponse | ErrorResponse> {
    const req = { ...getCartsReqObj };

    req.params = {
      projections,
    };

    return handleRequest(req, customOptions, options) as Promise<GetCartsResponse | ErrorResponse>;
  },
  getCartsByUserId(
    { projections = ApiProjections.DETAILED, userId }: CartsByUserIdPayloadType,
    customOptions?: CustomRequestOptions,
  ): Promise<CartAPIResponse> {
    const req = { ...getCartsReqObj };

    req.params = {
      projections: projections,
      userId: userId,
    };

    return handleRequest(req, customOptions);
  },
  createCart(
    {
      payload,
      projections = ApiProjections.DETAILED,
      options = {},
    }: ProjectionReqPayloadType & CartReqOptions & CreateCartPayloadType,
    customOptions?: CustomRequestOptions,
  ): Promise<CartAPIResponse> {
    const req = { ...createCartReqObj };

    req.body = payload as unknown as BodyInit;
    req.params = {
      projections,
    };

    return handleRequest(req, customOptions, options, true);
  },
  addToCart(
    {
      cartId,
      payload,
      subCartId,
      projections = ApiProjections.DETAILED,
      options = {},
    }: CartReqOptions & SubCartIdReqPayloadType & ProjectionReqPayloadType & AddToCartReqPayloadType,
    customOptions?: CustomRequestOptions,
  ): Promise<CartAPIResponse> {
    const req = { ...addToSubCartReqObj };
    let modifiedOptions = {};
    if (subCartId) {
      req.url = req.url.replace(SubCartId, subCartId);
      modifiedOptions = {
        ...options,
        skipCartIdStoreInLS: true,
      };
    }
    req.url = req.url.replace(CartId, cartId);

    req.body = payload as unknown as BodyInit;

    req.params = {
      projections,
    };

    return handleRequest(req, customOptions, modifiedOptions);
  },
  deleteCart(
    { options = {}, cartId, subCartIds }: requestOptPayloadType & SubCartIdsReqPayloadType,
    customOptions?: CustomRequestOptions,
  ): Promise<CartAPIResponse> {
    const req = { ...deleteCartReqObj };
    req.url = req.url.replace(CartId, cartId);

    req.params = {
      ...(subCartIds && { subCartIds: subCartIds }),
    };

    return handleRequest(req, customOptions, options);
  },
  clearCartEntries(
    { options = {}, cartId, subCartId }: requestOptPayloadType & SubCartIdReqPayloadType,
    customOptions?: CustomRequestOptions,
  ): Promise<CartAPIResponse> {
    const req = { ...clearSubCartEntriesReqObj };
    if (subCartId) {
      req.url = req.url.replace(SubCartId, subCartId);
    }
    req.url = req.url.replace(CartId, cartId);
    return handleRequest(req, customOptions, options);
  },
  updateCartEntries(
    {
      cartId,
      subCartId = cartId,
      payload,
      options = {},
    }: UpdateCartEntriesPayloadType & SubCartIdReqPayloadType & CartReqOptions,
    customOptions?: CustomRequestOptions,
  ): Promise<CartAPIResponse> {
    const req = { ...updateCartEntriesReqObj };
    req.url = req.url.replace(SubCartId, subCartId);
    req.url = req.url.replace(CartId, cartId);

    req.body = payload as unknown as BodyInit;
    return handleRequest(req, customOptions, options);
  },

  getProfiles(
    {
      cartId,
      searchString = '',
      offset,
      limit,
      aboAccountId,
    }: CartIdReqPayloadType & CartReqOptions & GetProfilePayloadType,
    customOptions?: CustomRequestOptions,
  ): Promise<AccountSearch> {
    const req = { ...getProfilesReqObj };
    req.url = req.url.replace(CartId, cartId);

    req.params = {
      query: searchString,
      offset,
      limit,
      ...(aboAccountId && { aboAccountId: aboAccountId }),
    };

    return handleRequest(req, customOptions) as Promise<AccountSearch>;
  },

  addToCartGCO(
    { cartId, payload, options = {} }: CartReqOptions & AddToCartReqPayloadType,
    customOptions?: CustomRequestOptions,
  ): Promise<CartAPIResponse> {
    const req = { ...addToCartGCOObj };

    req.url = req.url.replace(CartId, cartId);

    req.body = payload as unknown as BodyInit;

    req.params = {
      projections: ApiProjections.DETAILED,
    };

    return handleRequest(req, customOptions, options);
  },
  updateGCOFlag(
    {
      cartId,
      payload,
      subCartId,
      options = {},
    }: UpdateGCOFlagPayloadType & CartIdReqPayloadType & SubCartIdReqPayloadType & CartReqOptions,
    customOptions?: CustomRequestOptions,
  ): Promise<CartAPIResponse> {
    const req = { ...updateGCOFlagReqObj };
    req.url = req.url.replace(CartId, cartId);
    req.url = req.url.replace(SubCartId, subCartId ?? cartId);
    req.body = payload as unknown as BodyInit;

    return handleRequest(req, customOptions, options);
  },
  updateCart(
    cartId: string,
    subCartId: string,
    payload: UpdateCartRequest,
    options = {},
    customOptions?: CustomRequestOptions,
  ): Promise<CartAPIResponse> {
    const req = { ...updateCartReqObj };
    req.url = req.url.replace(CartId, cartId);
    req.url = req.url.replace(SubCartId, subCartId ?? cartId);
    req.body = payload as unknown as BodyInit;
    return handleRequest(req, customOptions, options);
  },

  redeemAPoints(
    payload: RedeemAmPointRequest,
    customOptions?: CustomRequestOptions,
    options?: Record<string, unknown>,
  ): Promise<RedeemAmPointResponse> {
    const req = { ...redeemReqObj };

    req.body = payload as unknown as BodyInit;
    return handleRequest(req, customOptions, options, true) as Promise<RedeemAmPointResponse>;
  },

  createSubCart(
    { cartId, payload, options = {} }: CreateSubCartPayloadType & CartReqOptions,
    customOptions?: CustomRequestOptions,
  ): Promise<CartAPIResponse> {
    const req = { ...createSubCartReqObj };
    req.url = req.url.replace(CartId, cartId);

    req.body = payload as unknown as BodyInit;
    return handleRequest(req, customOptions, options);
  },
  deleteSubCart(
    { cartId, subCartId, options = {} }: DeleteSubCartType & CartReqOptions,
    customOptions?: CustomRequestOptions,
  ): Promise<CartAPIResponse> {
    const req = { ...deleteSubCartReqObj };
    req.url = req.url.replace(CartId, cartId);
    req.url = req.url.replace(SubCartId, subCartId as string);

    return handleRequest(req, customOptions, options);
  },
  reassignCart(
    { subCartId, cartId, payload, options = {} }: ReassignCartPayloadType & CartIdReqPayloadType & CartReqOptions,
    customOptions?: CustomRequestOptions,
  ) {
    const req = { ...getReassignCartReqObj };
    req.url = req.url.replace(CartId, cartId);
    req.url = req.url.replace(SubCartId, subCartId as string);
    req.body = payload as unknown as BodyInit;

    return handleRequest(req, customOptions, options);
  },
  getSubCart(
    {
      subCartId,
      cartId,
      projections = ApiProjections.DETAILED,
      options = {},
    }: ProjectionReqPayloadType & DeleteSubCartType & CartReqOptions,
    customOptions?: CustomRequestOptions,
  ) {
    const req = { ...getSubCartReqObj };
    req.url = req.url.replace(CartId, cartId);
    req.url = req.url.replace(SubCartId, subCartId as string);

    req.params = {
      projections: projections,
    };

    return handleRequest(req, customOptions, options);
  },
  applyCoupon(
    { subCartId, cartId, payload, options = {} }: ProjectionReqPayloadType & ApplyCounponsPayload & CartReqOptions,
    customOptions?: CustomRequestOptions,
  ) {
    const req = { ...applyCouponReqObj };
    req.url = req.url.replace(CartId, cartId);
    req.url = req.url.replace(SubCartId, subCartId ?? (cartId as string));
    req.body = payload as unknown as BodyInit;

    return handleRequest(req, customOptions, options);
  },
  async mergeCart(): Promise<Cart> {
    const loggedInUserCartRes = (
      (await this.getCarts({
        options: {
          skipCartResStoreInLS: true,
          skipCartIdStoreInLS: true,
          skipVersionStoreInLS: true,
        },
      })) as GetCartsResponse
    ).carts;

    let loggedInUserCart: Cart;

    let loggedInUserHasCart = loggedInUserCartRes.length != 0;
    //Step 1 : Update cartid and version in localstorage for logged in user
    if (loggedInUserHasCart) {
      [loggedInUserCart] = loggedInUserCartRes;
      StorageService.getLocalStorage().store(CartConstantsEnum.CartId, loggedInUserCart?.id);
      StorageService.getLocalStorage().store(CartConstantsEnum.CartReqVersion, loggedInUserCart.version);
    } else {
      loggedInUserCart = await createEmptyCart();
      loggedInUserHasCart = loggedInUserCart ? true : false;
    }

    const anonymousUserCartData = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartObj) ?? [];
    // In Merge Cart we need to filter out registration entries
    const bundleValidatedEntries = validateBundleInEntries(anonymousUserCartData as unknown as CartEntryDetails[]);
    const filteredEntries = filterOutRegistrationProducts(bundleValidatedEntries);
    let cartResp = {} as Cart;
    if (filteredEntries.length) {
      const subCartId = isProxyOrder(loggedInUserCart?.orderType)
        ? loggedInUserCart?.subCarts?.find(({ proxySubCart }) => proxySubCart)?.subCartId
        : loggedInUserCart?.id;
      const payload = {
        subCarts: [
          {
            subCartId,
            entries: filteredEntries as unknown as CartEntry[],
          },
        ],
        version: Number(StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion)),
      };
      if (loggedInUserHasCart) {
        cartResp = (await this.addToCart({
          cartId: loggedInUserCart?.id,
          subCartId,
          payload,
        })) as Cart;
      } else {
        const isABO = SessionService.getSession().isABO();
        cartResp = (await this.createCart({
          payload: {
            subCarts: [
              {
                amwayUser: {},
                entries: anonymousUserCartData as unknown as CartEntry[],
              },
            ],
            orderType: isABO ? getOrderTypeConfig() : OrderType.Regular,
          },
        })) as Cart;
      }
    }

    return Object.keys(cartResp).length ? (cartResp as Cart) : loggedInUserCart;
  },
};

const replaceURI = (uri: string) => uri.replace(/[#]/g, encodeURIComponent('#'));

//private
const handleRequest = async (
  req: RequestOptions,
  customOptions?: CustomRequestOptions,
  options?: CartReqOtherOptions,
  skipIdempotency = false,
) => {
  if (customOptions?.headers) {
    req.headers = customOptions?.headers;
  }
  if (customOptions?.baseURL) {
    req.url = `${customOptions.baseURL}${req.url}`;
  }
  if (customOptions?.version) {
    req.url = req.url.replace(API_Version_CART, customOptions?.version);
  }

  let requestOptions = { ...req };
  requestOptions = appendWarehouseIdAndDeliveryTypeIntoReq(
    req,
    (req.body as BodyInitWH)?.['deliveryType'] || customOptions?.deliverytype,
  );
  requestOptions.url = replaceURI(req.url);
  if (skipIdempotency) {
    requestOptions.headers = {
      'X-Idempotency-Key': generateRandomHex(20),
    };
  }
  const tuples = ApiConfigService.getTuples();

  const response: Cart =
    tuples['X-Amway-Channel'] === Channel.Pos
      ? await request(requestOptions, {
          skipIdempotency,
        })
      : await requestWithVersionFix(requestOptions, {
          skipIdempotency,
        });

  if (options) {
    workWithStorageOptions(response, options);
  }

  return Promise.resolve(response);
};

const workWithStorageOptions = (response: Cart, options: CartReqOtherOptions = {}) => {
  const isUserAnonymous = SessionService.getSession().isAnonymous();
  if (!options.skipCartIdStoreInLS) {
    StorageService.getLocalStorage().store(CartConstantsEnum.CartId, response?.id as string);
  }
  if (!options.skipCartResStoreInLS && isUserAnonymous) {
    const mainCart = response.subCarts?.find(
      ({ subCartId }) => subCartId === StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
    ) as SubCarts;
    StorageService.getLocalStorage().store(
      CartConstantsEnum.CartObj,
      JSON.stringify(filterPromotionItemFromEntries(mainCart?.entries ?? [])),
    );
  }

  if (!options.skipIsGroupCartStoreInLS && response.orderType === OrderType.Gco) {
    StorageService.getLocalStorage().store(CartConstantsEnum.IsGroupCart, true);
  }

  if (!options.skipVersionStoreInLS) {
    StorageService.getLocalStorage().store(CartConstantsEnum.CartReqVersion, response.version as number);
  }
};

const appendWarehouseIdAndDeliveryTypeIntoReq = (req: RequestOptions, customDeliveryType?: string): RequestOptions => {
  const warehouseId = StorageService.getLocalStorage().retrieve(CartConstantsEnum.WarehouseId);
  const deliveryType = customDeliveryType
    ? customDeliveryType
    : CheckoutProfileService?.getCheckoutProfile()?.fulfillmentType?.id || '';

  if (req.method == HTTPVerbs.GET || req.method == HTTPVerbs.DELETE) {
    req.params = {
      ...(req.params as Record<string, string>),
      ...(warehouseId && { warehouseId: warehouseId }),
      deliveryType: deliveryType,
    };
  }
  if (req.method != HTTPVerbs.GET && req.method != HTTPVerbs.DELETE) {
    if (req.body) {
      (req.body as BodyInitWH)['warehouseId'] = warehouseId;
      (req.body as BodyInitWH)['deliveryType'] = deliveryType;
    }
  }
  return req;
};

export default cartApi;
